import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { FaTimes, FaEraser, FaPaintBrush, FaUndo } from 'react-icons/fa';

const ProductMaskEditor = ({ isOpen, file, onClose, onConfirm, initialMaskBlob, initialMaskOriginalSize }) => {
  const displayCanvasRef = useRef(null);
  const maskCanvasRef = useRef(null); // offscreen at original size
  const imageElementRef = useRef(null);
  const brushStampRef = useRef(null);

  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [scaleToOriginal, setScaleToOriginal] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  const [brushSize, setBrushSize] = useState(24);
  const [mode, setMode] = useState('brush'); // 'brush' | 'eraser'

  // Rebuild a radial-gradient brush stamp when size/scale changes
  useEffect(() => {
    const rebuildBrushStamp = () => {
      const radius = Math.max(2, Math.round(brushSize * scaleToOriginal));
      const size = Math.max(2, radius * 2);

      const stamp = document.createElement('canvas');
      stamp.width = size;
      stamp.height = size;
      const sctx = stamp.getContext('2d');

      // Radial gradient: solid center, gradual feather to edge
      const centerX = size / 2;
      const centerY = size / 2;
      const gradient = sctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        size / 2
      );
      // Inner 60% fully opaque, then feather to transparent at edge
      gradient.addColorStop(0.0, 'rgba(255,255,255,1)');
      gradient.addColorStop(0.6, 'rgba(255,255,255,1)');
      gradient.addColorStop(1.0, 'rgba(255,255,255,0)');

      sctx.clearRect(0, 0, size, size);
      sctx.fillStyle = gradient;
      sctx.beginPath();
      sctx.arc(centerX, centerY, size / 2, 0, Math.PI * 2);
      sctx.fill();

      brushStampRef.current = stamp;
    };

    rebuildBrushStamp();
  }, [brushSize, scaleToOriginal]);

  // Load image and setup canvases
  useEffect(() => {
    if (!isOpen || !file) return;

    const img = new Image();
    imageElementRef.current = img;
    const objectUrl = URL.createObjectURL(file);
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      setNaturalSize({ width: naturalWidth, height: naturalHeight });

      // determine display size (fit to max width while keeping aspect)
      // Use viewport-relative sizing to ensure it fits on screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const MAX_W = Math.min(800, viewportWidth * 0.7);
      const MAX_H = Math.min(600, viewportHeight * 0.6);
      let dispW = naturalWidth;
      let dispH = naturalHeight;
      const scaleW = MAX_W / naturalWidth;
      const scaleH = MAX_H / naturalHeight;
      const scale = Math.min(1, Math.min(scaleW, scaleH));
      dispW = Math.round(naturalWidth * scale);
      dispH = Math.round(naturalHeight * scale);

      setDisplaySize({ width: dispW, height: dispH });
      setScaleToOriginal(naturalWidth / dispW);

      // setup mask canvas (original size)
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = naturalWidth;
      maskCanvas.height = naturalHeight;
      const mctx = maskCanvas.getContext('2d');
      // Start fully transparent; paint adds alpha where brush is applied
      mctx.clearRect(0, 0, naturalWidth, naturalHeight);
      maskCanvasRef.current = maskCanvas;

      // If there is an initial mask, draw it onto the mask canvas scaled to original size
      if (initialMaskBlob) {
        const maskObjUrl = URL.createObjectURL(initialMaskBlob);
        const initMaskImg = new Image();
        initMaskImg.crossOrigin = 'anonymous';
        initMaskImg.onload = () => {
          const srcW = (initialMaskOriginalSize && initialMaskOriginalSize.width) || initMaskImg.naturalWidth || initMaskImg.width;
          const srcH = (initialMaskOriginalSize && initialMaskOriginalSize.height) || initMaskImg.naturalHeight || initMaskImg.height;
          const destW = maskCanvas.width;
          const destH = maskCanvas.height;
          const ctx2 = maskCanvas.getContext('2d');
          ctx2.clearRect(0, 0, destW, destH);
          ctx2.imageSmoothingEnabled = false;
          ctx2.drawImage(initMaskImg, 0, 0, srcW, srcH, 0, 0, destW, destH);
          drawPreview();
          URL.revokeObjectURL(maskObjUrl);
        };
        initMaskImg.onerror = () => {
          URL.revokeObjectURL(maskObjUrl);
          drawPreview();
        };
        initMaskImg.src = maskObjUrl;
      } else {
        // draw initial preview
        drawPreview();
      }
    };
    img.src = objectUrl;

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, file, initialMaskBlob, initialMaskOriginalSize]);

  const drawStrokeOnMask = (from, to) => {
    if (!maskCanvasRef.current) return;
    const mctx = maskCanvasRef.current.getContext('2d');
    const stamp = brushStampRef.current;
    if (!stamp) return;

    const radius = Math.max(2, Math.round(brushSize * scaleToOriginal));

    const stampAt = (x, y, composite) => {
      mctx.save();
      mctx.globalCompositeOperation = composite; // 'source-over' for brush, 'destination-out' for eraser
      mctx.drawImage(stamp, Math.round(x - stamp.width / 2), Math.round(y - stamp.height / 2));
      mctx.restore();
    };

    if (!to) {
      // single dab
      if (mode === 'brush') {
        stampAt(from.x, from.y, 'source-over');
      } else {
        stampAt(from.x, from.y, 'destination-out');
      }
      return;
    }

    // Stamp along the line for a smooth continuous stroke
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);
    const step = Math.max(1, radius * 0.3); // tighter spacing for smoother strokes
    const steps = Math.max(1, Math.ceil(distance / step));

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = from.x + dx * t;
      const y = from.y + dy * t;
      if (mode === 'brush') {
        stampAt(x, y, 'source-over');
      } else {
        stampAt(x, y, 'destination-out');
      }
    }
  };

  const getOriginalCoords = (e) => {
    const canvas = displayCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const xDisplay = (e.clientX) - rect.left;
    const yDisplay = (e.clientY) - rect.top;
    const x = Math.max(0, Math.min(displaySize.width, xDisplay)) * scaleToOriginal;
    const y = Math.max(0, Math.min(displaySize.height, yDisplay)) * scaleToOriginal;
    return { x, y };
  };

  const handlePointerDown = (e) => {
    if (!isOpen) return;
    const target = displayCanvasRef.current;
    if (target && typeof e.pointerId !== 'undefined' && target.setPointerCapture) {
      try { target.setPointerCapture(e.pointerId); } catch {}
    }
    const point = getOriginalCoords(e);
    setIsDrawing(true);
    setLastPoint(point);
    drawStrokeOnMask(point, null);
    drawPreview();
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const point = getOriginalCoords(e);
    drawStrokeOnMask(lastPoint, point);
    setLastPoint(point);
    drawPreview();
  };

  const handlePointerUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setLastPoint(null);
    const target = displayCanvasRef.current;
    if (target && typeof e.pointerId !== 'undefined' && target.releasePointerCapture) {
      try { target.releasePointerCapture(e.pointerId); } catch {}
    }
  };

  const resetMask = () => {
    if (!maskCanvasRef.current || !naturalSize.width) return;
    const mctx = maskCanvasRef.current.getContext('2d');
    mctx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
    drawPreview();
  };

  const drawPreview = () => {
    const canvas = displayCanvasRef.current;
    const img = imageElementRef.current;
    const maskCanvas = maskCanvasRef.current;
    if (!canvas || !img || !maskCanvas) return;

    const dispW = Math.max(1, displaySize.width);
    const dispH = Math.max(1, displaySize.height);
    canvas.width = dispW;
    canvas.height = dispH;
    const ctx = canvas.getContext('2d');

    // draw image first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // build overlay offscreen and punch out mask from overlay only
    const overlay = document.createElement('canvas');
    overlay.width = dispW;
    overlay.height = dispH;
    const octx = overlay.getContext('2d');
    octx.clearRect(0, 0, dispW, dispH);
    octx.fillStyle = 'rgba(0,0,0,0.5)';
    octx.fillRect(0, 0, dispW, dispH);
    octx.globalCompositeOperation = 'destination-out';
    octx.imageSmoothingEnabled = false;
    octx.drawImage(
      maskCanvas,
      0,
      0,
      maskCanvas.width,
      maskCanvas.height,
      0,
      0,
      dispW,
      dispH
    );

    // draw overlay on top of image
    ctx.drawImage(overlay, 0, 0);

    // optional mask outline for visual feedback
    ctx.save();
    ctx.globalAlpha = 0.25;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      maskCanvas,
      0,
      0,
      maskCanvas.width,
      maskCanvas.height,
      0,
      0,
      dispW,
      dispH
    );
    ctx.restore();
  };

  // Ensure preview updates after display size changes so image shows immediately on open
  useEffect(() => {
    if (!isOpen) return;
    if (!displaySize.width || !displaySize.height) return;
    if (!imageElementRef.current || !maskCanvasRef.current) return;
    drawPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displaySize, isOpen]);

  const confirmMask = async () => {
    if (!maskCanvasRef.current || !imageElementRef.current) return;

    // export mask blob (alpha encodes softness)
    const maskBlob = await new Promise((resolve) =>
      maskCanvasRef.current.toBlob((blob) => resolve(blob), 'image/png')
    );

    onConfirm({
      maskBlob,
      originalSize: { ...naturalSize }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 10 }}
        className="w-full max-w-[98vw] sm:max-w-[90vw] lg:max-w-[1000px] h-[95vh] sm:h-auto sm:max-h-[90vh] bg-gray-900/95 border border-gray-700/50 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-700/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm sm:text-base font-semibold">Highlight Product</span>
            <span className="hidden sm:inline text-xs text-gray-400">Freehand mask</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1.5 rounded-full hover:bg-white/10">
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Body - Canvas area takes priority */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Canvas area - Full height on mobile */}
          <div className="flex-1 min-h-0 p-2 sm:p-4 flex items-center justify-center bg-black/30 border-b sm:border-b-0 border-gray-700/50">
            <canvas
              ref={displayCanvasRef}
              className="touch-none cursor-crosshair max-w-full max-h-full"
              style={{ width: displaySize.width, height: displaySize.height, touchAction: 'none' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Tools - Compact horizontal layout on mobile, below canvas */}
          <div className="bg-gray-900/95 border-t border-gray-700/50 p-2 sm:p-3 flex-shrink-0">
            {/* Mobile/Tablet Layout (< lg) */}
            <div className="lg:hidden space-y-2">
              {/* Tool buttons row */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={() => setMode('brush')}
                  className={`flex-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all ${
                    mode === 'brush' 
                      ? 'bg-purple-600/30 border-purple-400/40 text-white' 
                      : 'bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  <FaPaintBrush className="inline mr-1 sm:mr-1.5 text-xs" />
                  <span className="hidden xs:inline">Brush</span>
                </button>
                <button
                  onClick={() => setMode('eraser')}
                  className={`flex-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all ${
                    mode === 'eraser' 
                      ? 'bg-purple-600/30 border-purple-400/40 text-white' 
                      : 'bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50'
                  }`}
                >
                  <FaEraser className="inline mr-1 sm:mr-1.5 text-xs" />
                  <span className="hidden xs:inline">Eraser</span>
                </button>
                <button
                  onClick={resetMask}
                  className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50 text-xs sm:text-sm transition-all"
                  title="Reset"
                >
                  <FaUndo className="text-xs" />
                </button>
              </div>

              {/* Brush size slider row */}
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">Size:</span>
                <input
                  type="range"
                  min="4"
                  max="64"
                  step="2"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <span className="text-xs text-gray-300 font-medium w-8 text-right flex-shrink-0">{brushSize}px</span>
              </div>

              {/* Action buttons row */}
              <div className="flex items-center gap-2 pt-1">
                <button 
                  onClick={onClose} 
                  className="flex-1 px-3 py-2 rounded-lg border bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50 text-xs sm:text-sm font-medium transition-all"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmMask} 
                  className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs sm:text-sm font-semibold shadow-lg hover:shadow-purple-500/50 transition-all"
                >
                  Use Product
                </button>
              </div>
            </div>

            {/* Desktop Layout (>= lg) - Hidden on mobile/tablet */}
            <div className="hidden lg:flex items-center justify-between gap-6">
              {/* Left: Tools */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Tool:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode('brush')}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      mode === 'brush' 
                        ? 'bg-purple-600/30 border-purple-400/40 text-white' 
                        : 'bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50'
                    }`}
                  >
                    <FaPaintBrush className="inline mr-2" /> Brush
                  </button>
                  <button
                    onClick={() => setMode('eraser')}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      mode === 'eraser' 
                        ? 'bg-purple-600/30 border-purple-400/40 text-white' 
                        : 'bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50'
                    }`}
                  >
                    <FaEraser className="inline mr-2" /> Eraser
                  </button>
                  <button
                    onClick={resetMask}
                    className="px-3 py-2 rounded-lg border bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50 text-sm transition-all"
                  >
                    <FaUndo className="inline mr-2" /> Reset
                  </button>
                </div>
              </div>

              {/* Center: Brush size */}
              <div className="flex items-center gap-3 flex-1 max-w-xs">
                <span className="text-sm text-gray-400 whitespace-nowrap">Brush size:</span>
                <input
                  type="range"
                  min="4"
                  max="64"
                  step="2"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
                <span className="text-sm text-gray-300 font-medium w-10 text-right">{brushSize}px</span>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2">
                <button 
                  onClick={onClose} 
                  className="px-4 py-2 rounded-lg border bg-gray-800/50 border-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-700/50 text-sm font-medium transition-all"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmMask} 
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold shadow-lg hover:shadow-purple-500/50 transition-all"
                >
                  Use Product
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductMaskEditor; 
